import React from 'react';
import { Progress } from 'reactstrap';
import { isEqual } from 'lodash-es';
import { v7 as uuid } from 'uuid';
import {
    faWarehouse, faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm, FlexCenterRow, FormDivider,
    Input, toasty,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { SaveButton } from '../common/forms/SaveButton';
import { ConfirmDelete } from '../common/Modal';
import { DispatchGroup } from './DispatchGroup';

class DispatchGroupForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            dispatchGroup: new DispatchGroup(),
            companies: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;

        this.onSubmit = this.onSubmit.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (prevProps && (this.props.match.params.id !== (prevProps.match.params ?? {}).id)) {
            this.populateState();
        }
    }

    onClearErrors = () => this.setState({ errors: {} });

    onSubmit = async (values) => {
        // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        if (this.props.location.pathname == AppNavPaths.DispatchGroupNew) {
            await this.create(values);
        } else {
            await this.update(values);
        }

        this.setSaving(false);
    };

    setSaving = (b) => this.setState({ saving: b });

    create = async (dispatchGroup) => {
        const newId = uuid();
        dispatchGroup.id = newId;

        const response = await httpClient.post(ApiRoutes.dispatchGroup.create(), dispatchGroup)
            .catch(this.handleSaveError);

        if (response) {
            toasty.success('Dispatch group created');
            this.context.history.push(`${AppNavPaths.DispatchGroup}/${newId}`);
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    async populateState() {
        const dispatchGroupId = this.props.match.params.id;

        const dispatchGroup = dispatchGroupId ? await httpClient.js(ApiRoutes.dispatchGroup.byId(dispatchGroupId)) : new DispatchGroup();
        const companies = await httpClient.js(ApiRoutes.typeAheads.allDispatchLocations());
        const originalData = clone(dispatchGroup);

        this.setState({
            originalData,
            dispatchGroup,
            companies,
            loading: false,
            saving: false,
        });
    }

    resetForm = () => this.setState({ formValidated: false });

    update = async (dispatchGroup) => {
        const response = await httpClient.put(ApiRoutes.dispatchGroup.byId(dispatchGroup.id), dispatchGroup)
            .catch(this.handleSaveError);

        if (response) {
            toasty.success('Save Successful');
        }
    };

    onDeleteGroupConfirmed = async () => {
        const { id } = { ...this.props.match.params };

        const response = await httpClient
            .delete(ApiRoutes.dispatchGroup.byId(id))
            .catch(this.handleSaveError);

        if (response) {
            toasty.success('Dispatch group deleted.');
            this.context.history.push(`${AppNavPaths.DispatchGroups}`);
        }
    };

    showDeleteModal = () => {
        ConfirmDelete.show({
            title: 'Delete Dispatch Group',
            onSubmit: () => this.onDeleteGroupConfirmed(),
            body: () => 'Are you sure you want to delete this dispatch group?',
        });
    };

    getActions = () => {
        const dispatchGroupId = this.props.match?.params?.id;

        const actions = [];

        if (dispatchGroupId) {
            actions.push({
                key: 'delete',
                onClick: () => this.showDeleteModal(),
                icon: faTrashAlt,
                label: 'Delete Dispatch Group',
                className: 'text-danger',
                CanProps: {
                    I: 'delete',
                    a: 'dispatch_group',
                },
            });
        }

        if (actions.length > 0) {
            return actions;
        }

        return null;
    };

    render() {
        const {
            dispatchGroup,
            companies,
            originalData,
            errors,
            formValidated,
            saving,
            loading,
        } = this.state;

        const existing = !!dispatchGroup.id;

        if (loading) {
            return <Progress />;
        }

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, dispatchGroup)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />

                <AppPageForm
                    backUrl={AppNavPaths.DispatchGroups}
                    backLabel="Back to Groups"
                    formShown={this.context.formIsOpen}
                    formId="groupForm"
                    formHeadingIcon={faWarehouse}
                    formHeading={!dispatchGroup.id ? 'New Dispatch Group' : 'Edit Dispatch Group'}
                    formName="groupForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    initialValues={dispatchGroup}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    loading={this.state.loading}
                    actions={this.getActions()}
                >
                    {() => (
                        <>
                            <Input
                                label="Name"
                                id="name"
                                name="name"
                                placeholder="Enter Name (max 150 characters)"
                                maxLength="150"
                                error="Name is required"
                                required
                            />

                            <ValidatedSelect
                                id="companies"
                                name="companies"
                                label="Companies"
                                required
                                isMulti
                                options={companies}
                                error="A company is required."
                                asPrimitive
                            />

                            <FormDivider />

                            <FlexCenterRow className="mb-3">
                                <SaveButton
                                    saving={!!this.state.saving}
                                    label={!existing ? 'Save New Group' : 'Save'}
                                />
                            </FlexCenterRow>
                        </>
                    )}
                </AppPageForm>
            </>
        );
    }
}
export default DispatchGroupForm;
